import {
  Delete as DeleteIcon,
  MoreHoriz as MoreHorizIcon,
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as RestartAltIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import ArticleIcon from "@mui/icons-material/Article";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import Popper from "@mui/material/Popper";
import { useContext, useEffect, useRef, useState } from "react";
import { useDempStore } from "src/context/stores";
import Tesseract from "tesseract.js";
import { StyledListItemButton } from "../../styles/style";
import DataSourceContext from "../../context/DataSourceMngContext";
import {
  createAITraining,
  useGetPolygonData,
} from "../../services/useTransactionApi";
import { useGetAITraining } from "../../services/useTransactionApi";

const COLOR_PALETTE = [
  "#FF4081",
  "#1976d2",
  "#00B8D9",
  "#FFD600",
  "#00C853",
  "#FF6D00",
  "#7C4DFF",
  "#FFC300",
  "#6C3483",
  "#FF5733",
];

const FIXED_ANNOTATION_FIELDS = [
  { value: "invoiceId", label: "Invoice Number" },
  { value: "companyTaxId", label: "Customer's Tax Id" },
  { value: "companyName", label: "Customer's Name" },
  { value: "companyAddress", label: "Customer's Address" },
  { value: "companyPhone", label: "Customer's Phone Number" },
  { value: "companyEmail", label: "Customer's Email" },
  { value: "supplierTypeTh", label: "Provider's Type (Thai)" },
  { value: "supplierTypeEn", label: "Provider's Type (English)" },
  { value: "supplierTaxId", label: "Provider's Tax Id" },
  { value: "supplierName", label: "Provider's Name" },
  { value: "supplierAddress", label: "Provider's Address" },
  { value: "supplierPhone", label: "Provider's Phone Number" },
  { value: "supplierEmail", label: "Provider's Email" },
  { value: "invoiceDate", label: "Invoice Date" },
  { value: "activityDate", label: "Active Date" },
  { value: "amount", label: "Consumption Amount" },
  { value: "totalCost", label: "Total Cost" },
  { value: "asset", label: "Asset" },
  { value: "billType", label: "Type of Bill" },
  { value: "type", label: "Type of Gasoline" },
];

export const DialogImageAnnotate = ({
  open,
  onClose,
  imageUrl: initialImageUrl,
  autoRunOcr = false,
}) => {
  const { plantId, companyId } = useDempStore();
  const { singleItem } = useGetAITraining(companyId, plantId);
  const detailsItem = singleItem;
  const {
    history,
    historyDetail,
    fetchHistoryDetail,
    setDataState,
    fetchDevice,
  } = useContext(DataSourceContext);
  const initialImageUrlValue =
    historyDetail?.blob_url || initialImageUrl || null;
  const [imageUrl, setImageUrl] = useState(initialImageUrlValue);
  const [billId, setBillId] = useState();

  const { polygonData } = useGetPolygonData(billId, imageUrl);
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // เพิ่ม state สำหรับ index ของรูปปัจจุบัน
  const [annotations, setAnnotations] = useState([]); // {label, value, box: {x0, y0, x1, y1}}
  const [labelColorMap, setLabelColorMap] = useState({});
  const [ocrWords, setOcrWords] = useState([]); // OCR words + bbox
  const [isOcrRunning, setIsOcrRunning] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [originalSize, setOriginalSize] = useState({ width: 0, height: 0 });
  const [clickMessage, setClickMessage] = useState("");
  const [hoveredWordIdx, setHoveredWordIdx] = useState(null);
  const [wordFieldMap, setWordFieldMap] = useState({});
  const [showWordHighlights, setShowWordHighlights] = useState(true);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  // Popper/Dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [activeWordIdx, setActiveWordIdx] = useState(null);
  const [annotationOptions, setAnnotationOptions] = useState(() => [
    ...FIXED_ANNOTATION_FIELDS.map((f) => ({
      ...f,
      color: COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
    })),
  ]);
  const [showDeleteIdx, setShowDeleteIdx] = useState(null);
  // state for multi-word selection
  const [selectedWordIdxs, setSelectedWordIdxs] = useState([]); // multi-word selection
  const [isSaving, setIsSaving] = useState(false); // state for save
  const [newLabel, setNewLabel] = useState("");
  const [savedPolygonData, setSavedPolygonData] = useState([]); // save data before submit
  const [isSubmitting, setIsSubmitting] = useState(false); // state for submit all
  const [fileStates, setFileStates] = useState({}); // state for each file
  const justSubmittedRef = useRef(false);
  const [baseScale, setBaseScale] = useState(1); // Scale to fit image in canvas
  const [zoomScale, setZoomScale] = useState(1); // Additional zoom factor
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });

  // Combined scale for drawing
  const scale = baseScale * zoomScale;
  // const panRef = useRef({ x: 0, y: 0 });
  // const scaleRef = useRef(1);

  // const [scale, setScale] = useState(1);

  const getColorForLabel = (label, labelColorMap, setLabelColorMap) => {
    if (labelColorMap[label]) return labelColorMap[label];
    const usedColors = Object.values(labelColorMap);
    const available =
      COLOR_PALETTE.find((c) => !usedColors.includes(c)) ||
      COLOR_PALETTE[usedColors.length % COLOR_PALETTE.length];
    setLabelColorMap((prev) => ({ ...prev, [label]: available }));
    return available;
  };

  // find billId from imageUrl
  const getBillIdFromImageUrl = (imageUrl, detailsItem) => {
    if (!imageUrl || !detailsItem || !Array.isArray(detailsItem)) return null;

    // find item that has blobUrl equals to imageUrl
    const foundItem = detailsItem.find((item) => item.blobUrl === imageUrl);
    return foundItem ? foundItem.billId : null;
  };

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("Canvas ref is null in redrawCanvas");
      return;
    }
    if (!imageRef.current) {
      console.error("Image ref is null in redrawCanvas");
      return;
    }
    if (originalSize.width === 0 || originalSize.height === 0) {
      console.log("Original size not set yet, skipping redraw");
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.error("Cannot get canvas context");
      return;
    }

    // Clear canvas and reset transform
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Apply pan and scale transformations
    ctx.translate(pan.x, pan.y);
    ctx.scale(scale, scale);

    // Draw image at origin (0,0) since we're already applying scale and offset
    ctx.drawImage(
      imageRef.current,
      0,
      0,
      imageRef.current.width,
      imageRef.current.height
    );

    // Reset transform for drawing overlays (OCR words and annotations)
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // draw highlight yellow
    if (showWordHighlights && ocrWords.length > 0) {
      ocrWords.forEach((word, idx) => {
        if (word.bbox) {
          // Scale bbox coordinates to canvas coordinates using combined scale (baseScale * zoomScale)
          // and apply pan offset
          const wordBox = {
            x0: word.bbox.x0 * scale + pan.x,
            y0: word.bbox.y0 * scale + pan.y,
            x1: word.bbox.x1 * scale + pan.x,
            y1: word.bbox.y1 * scale + pan.y,
          };

          // draw background of word
          ctx.save();
          if (selectedWordIdxs.includes(idx)) {
            ctx.globalAlpha = 0.35;
            ctx.fillStyle = "deepskyblue";
          } else {
            ctx.globalAlpha = 0.2;
            ctx.fillStyle = "yellow";
          }
          ctx.fillRect(
            wordBox.x0,
            wordBox.y0,
            wordBox.x1 - wordBox.x0,
            wordBox.y1 - wordBox.y0
          );
          ctx.restore();

          // border of word
          if (wordFieldMap[idx] !== undefined) {
            const fieldIdx = wordFieldMap[idx];
            ctx.save();
            ctx.strokeStyle = COLOR_PALETTE[fieldIdx % COLOR_PALETTE.length];
            ctx.lineWidth = 3;
            ctx.globalAlpha = 1.0;
            ctx.strokeRect(
              wordBox.x0,
              wordBox.y0,
              wordBox.x1 - wordBox.x0,
              wordBox.y1 - wordBox.y0
            );
            ctx.restore();
          } else if (hoveredWordIdx === idx) {
            ctx.save();
            ctx.strokeStyle = "deepskyblue";
            ctx.lineWidth = 3;
            ctx.globalAlpha = 1.0;
            ctx.strokeRect(
              wordBox.x0,
              wordBox.y0,
              wordBox.x1 - wordBox.x0,
              wordBox.y1 - wordBox.y0
            );
            ctx.restore();
          } else {
            ctx.save();
            ctx.strokeStyle = "yellow";
            ctx.lineWidth = 2;
            ctx.globalAlpha = 1.0;
            ctx.strokeRect(
              wordBox.x0,
              wordBox.y0,
              wordBox.x1 - wordBox.x0,
              wordBox.y1 - wordBox.y0
            );
            ctx.restore();
          }
        }
      });
    }

    // draw border of annotation (multiple boxes for one annotation)
    annotations.forEach((ann, idx) => {
      const color = ann.label
        ? getColorForLabel(ann.label, labelColorMap, setLabelColorMap)
        : "#FF4081";
      const boxes = ann.boxes || (ann.box ? [ann.box] : []);
      boxes.forEach((box) => {
        if (
          !box ||
          typeof box.x0 === "undefined" ||
          typeof box.y0 === "undefined" ||
          typeof box.x1 === "undefined" ||
          typeof box.y1 === "undefined"
        )
          return;

        // Scale annotation coordinates to canvas coordinates using combined scale (baseScale * zoomScale)
        // and apply pan offset
        const scaledBox = {
          x0: box.x0 * scale + pan.x,
          y0: box.y0 * scale + pan.y,
          x1: box.x1 * scale + pan.x,
          y1: box.y1 * scale + pan.y,
        };

        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.strokeRect(
          scaledBox.x0,
          scaledBox.y0,
          scaledBox.x1 - scaledBox.x0,
          scaledBox.y1 - scaledBox.y0
        );
      });
    });
  };

  const findWordAtPoint = (x, y) => {
    if (!ocrWords.length) return null;
    if (originalSize.width === 0 || originalSize.height === 0) return null;
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("Canvas ref is null in findWordAtPoint");
      return null;
    }

    // Convert mouse coordinates to canvas coordinates
    const px = x;
    const py = y;

    let foundIdx = null;
    ocrWords.forEach((word, idx) => {
      if (word.bbox) {
        // Scale bbox coordinates to canvas coordinates using combined scale (baseScale * zoomScale)
        // and apply pan offset
        const wordBox = {
          x0: word.bbox.x0 * scale + pan.x,
          y0: word.bbox.y0 * scale + pan.y,
          x1: word.bbox.x1 * scale + pan.x,
          y1: word.bbox.y1 * scale + pan.y,
        };
        if (
          wordBox.x0 <= px &&
          wordBox.x1 >= px &&
          wordBox.y0 <= py &&
          wordBox.y1 >= py
        ) {
          foundIdx = idx;
        }
      }
    });
    return foundIdx;
  };

  // Zoom
  const handleWheel = (e) => {
    e.preventDefault();

    // Calculate zoom factor based on wheel delta
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    let newScale = scale * zoomFactor;

    // Limit scale between 0.5 and 3
    newScale = Math.min(Math.max(0.5, newScale), 3);

    // Only update if zoom scale actually changed
    if (Math.abs(newScale - zoomScale) > 0.01) {
      setZoomScale(newScale);
      // Don't reset pan when zooming to maintain highlight positions
      // The pan position should remain the same to keep highlights in correct positions
    }
  };

  // Pan handlers
  const handleMouseDown = (e) => {
    if (e.button === 0) {
      // Left mouse button only
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      e.target.style.cursor = "grabbing";
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      const deltaX = e.clientX - lastPanPoint.x;
      const deltaY = e.clientY - lastPanPoint.y;

      setPan((prev) => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));

      setLastPanPoint({ x: e.clientX, y: e.clientY });
    } else {
      // Original hover logic for words
      if (originalSize.width === 0 || originalSize.height === 0) return;
      const canvas = canvasRef.current;
      if (!canvas) {
        console.error("Canvas ref is null in handleMouseMove");
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Convert mouse coordinates to canvas coordinates
      const px = x;
      const py = y;

      let foundIdx = null;
      ocrWords.forEach((word, idx) => {
        if (word.bbox) {
          // Scale bbox coordinates to canvas coordinates using combined scale (baseScale * zoomScale)
          // and apply pan offset
          const wordBox = {
            x0: word.bbox.x0 * scale + pan.x,
            y0: word.bbox.y0 * scale + pan.y,
            x1: word.bbox.x1 * scale + pan.x,
            y1: word.bbox.y1 * scale + pan.y,
          };
          if (
            wordBox.x0 <= px &&
            wordBox.x1 >= px &&
            wordBox.y0 <= py &&
            wordBox.y1 >= py
          ) {
            foundIdx = idx;
          }
        }
      });
      setHoveredWordIdx(foundIdx);
    }
  };

  const handleMouseUp = (e) => {
    if (e.button === 0) {
      setIsPanning(false);
      e.target.style.cursor = "pointer";
    }
  };

  const handleMouseLeave = () => {
    setIsPanning(false);
    setHoveredWordIdx(null);
  };

  // Function to clear all highlights
  const handleClearHighlights = () => {
    setOcrWords([]);
    setShowWordHighlights(false);
    setSelectedWordIdxs([]);
    setHoveredWordIdx(null);
    setWordFieldMap({});
    setAnnotations([]);
    setLabelColorMap({});
    setAnnotationOptions([
      ...FIXED_ANNOTATION_FIELDS.map((f) => ({
        ...f,
        color: COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
      })),
    ]);
  };

  // handle toggle each word (don't need to use shift)
  const handleCanvasClick = (e) => {
    if (!imageUrl) {
      setClickMessage("กรุณาอัพโหลดรูปก่อน");
      setTimeout(() => setClickMessage(""), 3000);
      return;
    }
    if (originalSize.width === 0 || originalSize.height === 0) {
      setClickMessage("กำลังโหลดรูป กรุณารอสักครู่");
      setTimeout(() => setClickMessage(""), 3000);
      return;
    }
    if (isOcrRunning) {
      setClickMessage("กำลังประมวลผล OCR กรุณารอสักครู่");
      setTimeout(() => setClickMessage(""), 3000);
      return;
    }
    if (ocrWords.length === 0) {
      setClickMessage("ไม่พบข้อความในรูป กรุณาลองรูปอื่น");
      setTimeout(() => setClickMessage(""), 3000);
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("Canvas ref is null in handleCanvasClick");
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const wordIdx = findWordAtPoint(x, y);
    if (wordIdx === null) {
      setClickMessage("ไม่พบคำที่คลิก ลองคลิกใกล้คำมากขึ้น");
      setTimeout(() => setClickMessage(""), 3000);
      return;
    }
    setSelectedWordIdxs((prev) =>
      prev.includes(wordIdx)
        ? prev.filter((idx) => idx !== wordIdx)
        : [...prev, wordIdx]
    );
    setDropdownPosition({ x: e.clientX, y: e.clientY });
    setDropdownOpen(true);
  };

  const convertPolygonToBbox = (polygon) => {
    if (!polygon) return null;
    if (!Array.isArray(polygon)) return null;
    if (polygon.length !== 4) return null;
    const [x0, y0, x1, y1] = polygon;
    if (
      typeof x0 !== "number" ||
      typeof y0 !== "number" ||
      typeof x1 !== "number" ||
      typeof y1 !== "number"
    )
      return null;
    if (x1 <= x0 || y1 <= y0) return null;
    return { x0, y0, x1, y1 };
  };

  const convertOcrData = (ocrData) => {
    if (!Array.isArray(ocrData)) return [];
    return ocrData
      .map((item) => {
        if (!item.text) return null;
        const bbox = convertPolygonToBbox(item.polygon);
        if (!bbox) return null;
        return { text: item.text, bbox };
      })
      .filter((word) => word !== null);
  };

  const handleSave = async () => {
    if (!imageUrl || !originalSize.width || !originalSize.height) return;

    setIsSaving(true);
    try {
      // file name from detailsItem or imageUrl
      let fileName = "image.jpg";
      if (detailsItem && detailsItem[currentImageIndex]) {
        fileName = detailsItem[currentImageIndex].blobUrl
          .split("/")
          .pop()
          .split("?")[0];
      } else if (imageUrl.startsWith("data:")) {
        if (imageUrl.name) fileName = imageUrl.name;
      } else {
        fileName = imageUrl.split("/").pop().split("?")[0];
      }

      // use billId that already saved
      const currentBillId =
        billId || detailsItem?.[currentImageIndex]?.billId || 0;

      const labels = annotationOptions
        .map((opt) => {
          const ann = annotations.find((a) => a.label === opt.value);
          if (!ann || !(ann.boxes || ann.box)) return null;
          const boxesArr = ann.boxes || (ann.box ? [ann.box] : []);

          let texts = [];
          if (typeof ann.value === "string") {
            texts = ann.value.split(" ");
          } else if (Array.isArray(ann.value)) {
            texts = ann.value;
          }

          while (texts.length < boxesArr.length) {
            texts.push(texts[texts.length - 1] || "");
          }
          const values = boxesArr.map((box, idx) => {
            // Convert canvas coordinates back to original image coordinates using combined scale (baseScale * zoomScale)
            // and remove pan offset
            const originalBox = {
              x0: (box.x0 - pan.x) / scale,
              y0: (box.y0 - pan.y) / scale,
              x1: (box.x1 - pan.x) / scale,
              y1: (box.y1 - pan.y) / scale,
            };

            // Ensure coordinates are within bounds
            const clampedBox = {
              x0: Math.max(0, Math.min(originalBox.x0, originalSize.width)),
              y0: Math.max(0, Math.min(originalBox.y0, originalSize.height)),
              x1: Math.max(0, Math.min(originalBox.x1, originalSize.width)),
              y1: Math.max(0, Math.min(originalBox.y1, originalSize.height)),
            };

            const norm = (p) => [
              +(p.x0 / originalSize.width),
              +(p.y0 / originalSize.height),
              +(p.x1 / originalSize.width),
              +(p.y0 / originalSize.height),
              +(p.x1 / originalSize.width),
              +(p.y1 / originalSize.height),
              +(p.x0 / originalSize.width),
              +(p.y1 / originalSize.height),
            ];
            return {
              page: 1,
              text: texts[idx] || ann.value,
              boundingBoxes: [norm(clampedBox)],
            };
          });
          return {
            label: opt.value,
            key: null,
            value: values,
          };
        })
        .filter(Boolean);

      // create data for current file
      const currentFileData = {
        billId: currentBillId,
        document: fileName,
        labels: labels,
      };

      // check if the file has been saved or not
      const existingIndex = savedPolygonData.findIndex(
        (item) => item.billId === currentBillId
      );

      if (existingIndex >= 0) {
        // update existing data
        setSavedPolygonData((prev) =>
          prev.map((item, index) =>
            index === existingIndex ? currentFileData : item
          )
        );
      } else {
        // add new data
        setSavedPolygonData((prev) => [...prev, currentFileData]);
      }

      setDataState((prevState) => ({
        ...prevState,
        alert: {
          open: true,
          message: "File saved successfully",
          severity: "success",
        },
      }));
    } catch (error) {
      console.error("Error in handleSave:", error);
      setDataState((prevState) => ({
        ...prevState,
        alert: {
          open: true,
          message: "save error",
          severity: "error",
        },
      }));
    } finally {
      setIsSaving(false);
    }
  };

  // for update imageUrl when historyDetail changed
  useEffect(() => {
    if (historyDetail?.blob_url) {
      setImageUrl(historyDetail.blob_url);
      setBillId(getBillIdFromImageUrl(historyDetail.blob_url, detailsItem));
      setAnnotations([]);
      setLabelColorMap({});
      // reset annotationOptions to FIXED_ANNOTATION_FIELDS
      setAnnotationOptions([
        ...FIXED_ANNOTATION_FIELDS.map((f) => ({
          ...f,
          color:
            COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
        })),
      ]);
      setWordFieldMap({});
      // reset ocrWords when imageUrl changed
      setOcrWords([]);
      setShowWordHighlights(false);
      setIsOcrRunning(false);
      setOcrProgress(0);
      // reset originalSize when imageUrl changed
      setOriginalSize({ width: 0, height: 0 });
      // reset pan and scale when image changes - will be set in useEffect when image loads
      setPan({ x: 0, y: 0 });
      setZoomScale(1);
      setBaseScale(1);
    } else if (initialImageUrl) {
      // if historyDetail?.blob_url doesn't have value and initialImageUrl exists, use initialImageUrl
      setImageUrl(initialImageUrl);
      setBillId(getBillIdFromImageUrl(initialImageUrl, detailsItem));
      setAnnotations([]);
      setLabelColorMap({});
      // reset annotationOptions to FIXED_ANNOTATION_FIELDS
      setAnnotationOptions([
        ...FIXED_ANNOTATION_FIELDS.map((f) => ({
          ...f,
          color:
            COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
        })),
      ]);
      setWordFieldMap({});
      // reset ocrWords when imageUrl changed
      setOcrWords([]);
      setShowWordHighlights(false);
      setIsOcrRunning(false);
      setOcrProgress(0);
      // reset originalSize when imageUrl changed
      setOriginalSize({ width: 0, height: 0 });
      setBaseScale(1);
    } else if (detailsItem && detailsItem.length > 0) {
      // if there is no historyDetail or initialImageUrl, but there is detailsItem, use the first item
      setImageUrl(detailsItem[0].blobUrl);
      setBillId(detailsItem[0].billId);
      setCurrentImageIndex(0);
      setAnnotations([]);
      setLabelColorMap({});
      // reset annotationOptions to FIXED_ANNOTATION_FIELDS
      setAnnotationOptions([
        ...FIXED_ANNOTATION_FIELDS.map((f) => ({
          ...f,
          color:
            COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
        })),
      ]);
      setWordFieldMap({});
      // reset ocrWords when imageUrl changed
      setOcrWords([]);
      setShowWordHighlights(false);
      setIsOcrRunning(false);
      setOcrProgress(0);
      // reset originalSize when imageUrl changed
      setOriginalSize({ width: 0, height: 0 });
      setBaseScale(1);
    }
  }, [historyDetail, initialImageUrl, detailsItem]);

  // for uploading the first image when modal opened
  useEffect(() => {
    if (open && !imageUrl && detailsItem && detailsItem.length > 0) {
      // when modal opened and imageUrl doesn't have value and detailsItem exists, use the first item
      setImageUrl(detailsItem[0].blobUrl);
      setBillId(detailsItem[0].billId);
      setCurrentImageIndex(0);
      setAnnotations([]);
      setLabelColorMap({});
      setAnnotationOptions([
        ...FIXED_ANNOTATION_FIELDS.map((f) => ({
          ...f,
          color:
            COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
        })),
      ]);
      setWordFieldMap({});
      setOcrWords([]);
      setShowWordHighlights(false);
      setIsOcrRunning(false);
      setOcrProgress(0);
      // reset originalSize when imageUrl changed
      setOriginalSize({ width: 0, height: 0 });
      // reset pan and scale when image changes - will be set in useEffect when image loads
      setPan({ x: 0, y: 0 });
      setZoomScale(1);
      setBaseScale(1);
    }
  }, [open, imageUrl, detailsItem]);

  useEffect(() => {
    if (
      open &&
      autoRunOcr &&
      imageUrl &&
      !isOcrRunning &&
      ocrWords.length === 0
    ) {
      setIsOcrRunning(true);
      setOcrProgress(0);

      // Simulate OCR progress
      const progressInterval = setInterval(() => {
        setOcrProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      setTimeout(() => {
        clearInterval(progressInterval);
        setOcrProgress(100);
        const convertedWords = convertOcrData(polygonData?.lines);
        setOcrWords(convertedWords);
        setShowWordHighlights(true);
        setTimeout(() => setClickMessage(""), 3000);
        setIsOcrRunning(false);
      }, 1500); // Simulate 1.5 seconds processing time
    }
  }, [open, autoRunOcr, imageUrl, isOcrRunning, ocrWords.length, detailsItem]);

  // for drawing canvas when imageUrl, annotations, ocrWords changed
  useEffect(() => {
    if (!open || !imageUrl) {
      console.log("Modal not open or no imageUrl, skipping canvas drawing");
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      const newOriginalSize = { width: img.width, height: img.height };
      setOriginalSize(newOriginalSize);
      const canvas = canvasRef.current;

      // check if canvas exists or not
      if (!canvas) {
        console.error("Canvas ref is null, cannot set properties");
        return;
      }

      // Set canvas to fixed size 800x600
      canvas.width = 500;
      canvas.height = 500;

      // Calculate scale to fit image within canvas while maintaining aspect ratio
      const maxWidth = 500;
      const maxHeight = 500;
      const scaleX = maxWidth / img.width;
      const scaleY = maxHeight / img.height;
      const scale = Math.min(scaleX, scaleY);

      // Calculate centered position
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const offsetX = (maxWidth - scaledWidth) / 2;
      const offsetY = (maxHeight - scaledHeight) / 2;

      // Store base scale and offset for drawing
      setBaseScale(scale);
      setPan({ x: offsetX, y: offsetY });

      // Set display size to fixed dimensions
      canvas.style.width = `${maxWidth}px`;
      canvas.style.height = `${maxHeight}px`;
      imageRef.current = img;

      // wait for originalSize to update before drawing canvas
      setTimeout(() => {
        redrawCanvas();
      }, 0);
    };
    img.onerror = (error) => {
      console.error("Error loading image:", error);
      console.error("Image URL:", imageUrl);
    };

    // Double check imageUrl is not undefined before setting src
    if (imageUrl) {
      img.src = imageUrl;
    } else {
      console.warn("imageUrl is undefined, cannot load image");
    }
  }, [
    open,
    imageUrl,
    annotations,
    labelColorMap,
    ocrWords,
    hoveredWordIdx,
    wordFieldMap,
    showWordHighlights,
    selectedWordIdxs,
  ]);

  // for redraw canvas when originalSize changed
  useEffect(() => {
    if (originalSize.width > 0 && originalSize.height > 0 && imageRef.current) {
      redrawCanvas();
    }
  }, [
    originalSize,
    annotations,
    labelColorMap,
    ocrWords,
    hoveredWordIdx,
    wordFieldMap,
    showWordHighlights,
    selectedWordIdxs,
  ]);

  // Separate effect for scale changes to avoid unnecessary redraws
  useEffect(() => {
    if (originalSize.width > 0 && originalSize.height > 0 && imageRef.current) {
      redrawCanvas();
    }
  }, [scale, pan]);

  // annotation
  const handleAddAnnotation = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    // don't use value (use key as value)
    if (!annotationOptions.find((opt) => opt.value === trimmed)) {
      // random color
      const randomColor =
        COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)];
      setAnnotationOptions((prev) => [
        ...prev,
        { value: trimmed, label: trimmed, color: randomColor },
      ]);
    }
    if (!annotations.find((a) => a.label === trimmed)) {
      getColorForLabel(trimmed, labelColorMap, setLabelColorMap);
      setAnnotations([
        ...annotations,
        { label: trimmed, value: "", box: null },
      ]);
    }
    setNewLabel("");
  };

  // function for changing image
  const handleImageChange = (index) => {
    handleClearHighlights();
    // Save current state before changing image
    setFileStates((prev) => ({
      ...prev,
      [currentImageIndex]: {
        annotations,
        labelColorMap,
        annotationOptions,
        wordFieldMap,
        ocrWords,
        showWordHighlights,
        isOcrRunning,
        ocrProgress,
        selectedWordIdxs,
        dropdownOpen,
        originalSize, // add originalSize to save the size of the image
      },
    }));

    // Load new state or default
    const newState = fileStates[index] || {};
    setCurrentImageIndex(index);
    setImageUrl(detailsItem[index].blobUrl);
    setBillId(detailsItem[index].billId);
    setAnnotations(newState.annotations || []);
    setLabelColorMap(newState.labelColorMap || {});
    setAnnotationOptions(
      newState.annotationOptions || [
        ...FIXED_ANNOTATION_FIELDS.map((f) => ({
          ...f,
          color:
            COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
        })),
      ]
    );
    setWordFieldMap(newState.wordFieldMap || {});
    setOcrWords(newState.ocrWords || []);
    setShowWordHighlights(newState.showWordHighlights || false);
    setIsOcrRunning(newState.isOcrRunning || false);
    setOcrProgress(newState.ocrProgress || 0);
    setSelectedWordIdxs(newState.selectedWordIdxs || []);
    setDropdownOpen(newState.dropdownOpen || false);

    // Reset scale and pan when changing image
    setZoomScale(1);
    setBaseScale(1);
    setPan({ x: 0, y: 0 });
    setOriginalSize({ width: 0, height: 0 });
  };

  // function for submitting all data
  const handleSubmitAll = async () => {
    if (savedPolygonData.length === 0) {
      setDataState((prevState) => ({
        ...prevState,
        alert: {
          open: true,
          message: "No data to submit",
          severity: "warning",
        },
      }));
      fetchDevice();
      return;
    }

    setIsSubmitting(true);
    try {
      const finalData = {
        companyId: companyId,
        plantId: plantId,
        polygonData: savedPolygonData,
      };

      const result = await createAITraining(finalData);

      if (result.error) {
        console.error("Error submitting data:", result.error);
        setDataState((prevState) => ({
          ...prevState,
          alert: {
            open: true,
            message: "submit error",
            severity: "error",
          },
        }));
      } else {
        setDataState((prevState) => ({
          ...prevState,
          alert: {
            open: true,
            message: "submit success",
            severity: "success",
          },
        }));
        setSavedPolygonData([]);
        justSubmittedRef.current = true;
        onClose();
      }
    } catch (error) {
      console.error("Error in handleSubmitAll:", error);
      setDataState((prevState) => ({
        ...prevState,
        alert: {
          open: true,
          message: "submit error",
          severity: "error",
        },
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // for resetting state when modal opened and submit
  useEffect(() => {
    if (open && justSubmittedRef.current) {
      setAnnotations([]);
      setLabelColorMap({});
      setAnnotationOptions([
        ...FIXED_ANNOTATION_FIELDS.map((f) => ({
          ...f,
          color:
            COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
        })),
      ]);
      setWordFieldMap({});
      setOcrWords([]);
      setShowWordHighlights(false);
      setIsOcrRunning(false);
      setOcrProgress(0);
      setSelectedWordIdxs([]);
      setDropdownOpen(false);
      setSavedPolygonData([]);
      setFileStates({});
      setCurrentImageIndex(0);
      // reset originalSize when modal opened and submit
      setOriginalSize({ width: 0, height: 0 });
      // reset pan and scale when modal opens - will be set in useEffect when image loads
      setPan({ x: 0, y: 0 });
      setZoomScale(1);
      setBaseScale(1);
      justSubmittedRef.current = false;
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      sx={{
        overflow: "visible",
        "& .MuiPaper-root": { maxHeight: "80vh" },
      }}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <Typography sx={{ fontSize: "20px", fontWeight: 500, padding: "20px" }}>
        Image Annotation
      </Typography>
      <DialogContent>
        <Grid container>
          {/* Image Selection Sidebar */}
          {/* Image Selection Sidebar */}
          {detailsItem && detailsItem.length > 1 && (
            <Grid item xs={12} md={2}>
              <List>
                {detailsItem.map((item, index) => (
                  <ListItem sx={{ padding: "3px" }}>
                    <StyledListItemButton
                      selected={currentImageIndex === index}
                      onClick={() => handleImageChange(index)}
                    >
                      <ListItemIcon>
                        {currentImageIndex === index ? (
                          <DocumentScannerIcon
                            sx={{ color: "rgb(164, 82, 0)" }}
                          />
                        ) : (
                          <ArticleIcon />
                        )}
                      </ListItemIcon>
                      <ListItemText primary={`File ${index + 1}`} />
                    </StyledListItemButton>
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                padding: "0 40px",
              }}
            >
              <Typography variant="h6" gutterBottom>
                {detailsItem && detailsItem[currentImageIndex] && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 0.5 }}
                  >
                    Page {detailsItem[currentImageIndex].page || 1} •{" "}
                    {detailsItem[currentImageIndex].fileType?.toUpperCase() ||
                      "IMG"}
                  </Typography>
                )}
              </Typography>
              <Box
                sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}
              >
                {/* <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleImageUpload}
                />
                <label htmlFor="image-upload">
                  <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                    Upload Image
                  </Button>
                </label> */}
                {imageUrl && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setIsOcrRunning(true);
                      setOcrProgress(0);

                      // Simulate OCR progress
                      const progressInterval = setInterval(() => {
                        setOcrProgress((prev) => {
                          if (prev >= 90) {
                            clearInterval(progressInterval);
                            return 90;
                          }
                          return prev + 10;
                        });
                      }, 100);

                      setTimeout(() => {
                        clearInterval(progressInterval);
                        setOcrProgress(100);
                        const convertedWords = convertOcrData(
                          polygonData?.lines
                        );
                        setOcrWords(convertedWords);
                        setShowWordHighlights(true);
                        setTimeout(() => setClickMessage(""), 3000);
                        setIsOcrRunning(false);
                      }, 1500); // Simulate 1.5 seconds processing time
                    }}
                  >
                    Run Layout
                  </Button>
                )}
              </Box>
            </Box>
            {isOcrRunning && (
              <Box sx={{ width: "100%", mb: 2, padding: "0 40px" }}>
                <LinearProgress variant="determinate" value={ocrProgress} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Processing OCR... {ocrProgress}%
                </Typography>
              </Box>
            )}
            {imageUrl && (
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {/* Zoom Controls */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    justifyContent: "center",
                    alignItems: "center",
                    position: "absolute",
                    top: 10,
                    left: 40,
                    zIndex: 1000,
                    padding: "5px",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ZoomOutIcon />}
                    onClick={() => {
                      const newZoom = Math.max(0.5, zoomScale * 0.8);
                      setZoomScale(newZoom);
                      // Don't reset pan when zooming to maintain highlight positions
                    }}
                    sx={{ minWidth: "80px", height: "32px" }}
                  >
                    Zoom Out
                  </Button>
                  <Typography
                    variant="body2"
                    sx={{ minWidth: "60px", textAlign: "center" }}
                  >
                    {Math.round(zoomScale * 100)}%
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ZoomInIcon />}
                    onClick={() => {
                      const newZoom = Math.min(3, zoomScale * 1.2);
                      setZoomScale(newZoom);
                      // Don't reset pan when zooming to maintain highlight positions
                    }}
                    sx={{ minWidth: "80px", height: "32px" }}
                  >
                    Zoom In
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RestartAltIcon />}
                    onClick={() => {
                      setZoomScale(1);
                      // Reset pan to center when using reset button
                      if (originalSize.width > 0 && originalSize.height > 0) {
                        const maxWidth = 500;
                        const maxHeight = 500;
                        const scaleX = maxWidth / originalSize.width;
                        const scaleY = maxHeight / originalSize.height;
                        const baseScaleValue = Math.min(scaleX, scaleY);
                        const scaledWidth = originalSize.width * baseScaleValue;
                        const scaledHeight =
                          originalSize.height * baseScaleValue;
                        const offsetX = (maxWidth - scaledWidth) / 2;
                        const offsetY = (maxHeight - scaledHeight) / 2;
                        setPan({ x: offsetX, y: offsetY });
                      }
                    }}
                    sx={{ minWidth: "80px", height: "32px" }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={handleClearHighlights}
                    sx={{ minWidth: "80px", height: "32px" }}
                  >
                    Clear
                  </Button>
                </Box>
                <canvas
                  ref={canvasRef}
                  style={{
                    border: "1px solid #ccc",
                    backgroundColor: "rgba(227, 224, 224, 0.5)",
                    cursor: "pointer",
                    borderRadius: "12px",
                  }}
                  onClick={handleCanvasClick}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onWheel={handleWheel}
                />
                {/* Popper Dropdown Annotation */}
                <Popper
                  open={dropdownOpen}
                  anchorEl={{
                    getBoundingClientRect: () => ({
                      left: dropdownPosition.x + 16,
                      top: dropdownPosition.y + 16,
                      right: dropdownPosition.x + 16,
                      bottom: dropdownPosition.y + 16,
                      width: 0,
                      height: 0,
                    }),
                  }}
                  placement="right-start"
                  style={{ zIndex: 1302 }}
                >
                  <Autocomplete
                    autoFocus
                    options={annotationOptions}
                    getOptionLabel={(option) => option.label}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 6,
                            background: option.color,
                            display: "inline-block",
                          }}
                        />
                        {option.label}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="เลือก annotation"
                        size="small"
                        autoFocus
                      />
                    )}
                    onChange={(e, value) => {
                      setDropdownOpen(false);
                      if (value && selectedWordIdxs.length > 0) {
                        // รวมข้อความ
                        const texts = selectedWordIdxs
                          .sort((a, b) => a - b)
                          .map((idx) => ocrWords[idx]?.text)
                          .join("");
                        // รวม bbox
                        const bboxes = selectedWordIdxs
                          .map((idx) => ocrWords[idx]?.bbox)
                          .filter(Boolean);
                        if (bboxes.length === 0) return;

                        // Convert from original image coordinates to canvas coordinates using combined scale (baseScale * zoomScale)
                        // and apply pan offset
                        const box = {
                          x0: Math.min(
                            ...bboxes.map((b) => b.x0 * scale + pan.x)
                          ),
                          y0: Math.min(
                            ...bboxes.map((b) => b.y0 * scale + pan.y)
                          ),
                          x1: Math.max(
                            ...bboxes.map((b) => b.x1 * scale + pan.x)
                          ),
                          y1: Math.max(
                            ...bboxes.map((b) => b.y1 * scale + pan.y)
                          ),
                        };
                        setAnnotations((prev) => {
                          const exists = prev.find(
                            (a) => a.label === value.value
                          );
                          if (exists) {
                            return prev.map((a) =>
                              a.label === value.value
                                ? {
                                    ...a,
                                    value: a.value
                                      ? a.value + " " + texts
                                      : texts,
                                    boxes: a.boxes
                                      ? [...a.boxes, box]
                                      : a.box
                                      ? [a.box, box]
                                      : [box],
                                  }
                                : a
                            );
                          } else {
                            return [
                              ...prev,
                              {
                                label: value.value,
                                value: texts,
                                boxes: [box],
                              },
                            ];
                          }
                        });
                        setSelectedWordIdxs([]);
                      }
                    }}
                    onClose={() => setDropdownOpen(false)}
                    disableClearable
                    openOnFocus
                    sx={{
                      minWidth: 220,
                      bgcolor: "white",
                      boxShadow: 3,
                      borderRadius: 2,
                      p: 1,
                    }}
                  />
                </Popper>
              </Box>
            )}
            {!imageUrl && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  textAlign: "center",
                  color: "gray",
                  border: "1px dashed #ccc",
                }}
              >
                <Typography variant="body1">No image available</Typography>
                <Typography variant="body2">
                  imageUrl: {imageUrl || "null/undefined"}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                padding: "5px",
                borderRadius: "12px",
              }}
            >
              <Typography sx={{ padding: "5px", fontSize: "16px" }}>
                Annotations
              </Typography>
              <Box>
                <Box
                  sx={{ display: "flex", gap: 0.5, padding: "0 5px 10px 5px" }}
                >
                  <TextField
                    size="small"
                    placeholder="Add new annotation"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleAddAnnotation()
                    }
                    sx={{ width: "100%" }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddAnnotation}
                    startIcon={<SaveIcon />}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ maxHeight: 500, overflowY: "auto", padding: "5px" }}>
                  {annotationOptions.map((opt, idx) => {
                    const ann = annotations.find((a) => a.label === opt.value);
                    const color = opt.color;
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 1.5,
                          borderRadius: 2,
                          border: "1px solid #e0e0e0",
                          background: ann ? "#f9fbfd" : "#fafafa",
                          position: "relative",
                          minHeight: 48,
                        }}
                      >
                        <Box
                          sx={{
                            width: 22,
                            fontWeight: 500,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                            mr: 1,
                            mt: "2px",
                          }}
                        >
                          {idx + 1}
                        </Box>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            background: color,
                            mr: 1,
                            mt: "5px",
                          }}
                        />
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              fontWeight: 500,
                              color: "#222",
                              fontSize: 15,
                              lineHeight: 1.2,
                            }}
                          >
                            {opt.label}
                          </Box>
                          {ann?.value && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "#222",
                                fontSize: 15,
                                mt: 0.5,
                                wordBreak: "break-word",
                                whiteSpace: "pre-line",
                                lineHeight: 1.2,
                              }}
                            >
                              <span style={{ flex: 1 }}>{ann.value}</span>
                              <IconButton
                                size="small"
                                sx={{ ml: 1, p: "2px", color: "#e53935" }}
                                onClick={() => {
                                  setAnnotations((prev) =>
                                    prev.filter((a) => a.label !== opt.value)
                                  );
                                }}
                              >
                                X
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteIdx(
                                showDeleteIdx === idx ? null : idx
                              );
                            }}
                          >
                            <MoreHorizIcon />
                          </IconButton>
                          {showDeleteIdx === idx && (
                            <IconButton
                              sx={{ mt: 0.5 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAnnotationOptions((prev) =>
                                  prev.filter((a) => a.label !== opt.label)
                                );
                                setAnnotations((prev) =>
                                  prev.filter((a) => a.label !== opt.label)
                                );
                                setShowDeleteIdx(null);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              {/* {annotations.length > 0 && (
                  <Button variant="contained" color="success" startIcon={<SaveIcon />} sx={{ mt: 2 }} onClick={handleExport}>
                    EXPORT ANNOTATION (JSON)
                  </Button>
                )} */}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Saved files: {savedPolygonData.length}
            </Typography>
            {savedPolygonData.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {savedPolygonData.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "success.main",
                      display: "inline-block",
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isSaving}
              startIcon={<SaveIcon />}
              onClick={handleSave}
            >
              {isSaving ? "Saving..." : `Save File ${currentImageIndex + 1}`}
            </Button>
            {savedPolygonData.length > 0 && (
              <Button
                variant="contained"
                color="success"
                disabled={isSubmitting}
                onClick={handleSubmitAll}
              >
                {isSubmitting ? "Submitting..." : "Done"}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
